import * as React from 'react'
import CurrencyInputBase, { type CurrencyInputOnChangeValues } from 'react-currency-input-field'
import { cn } from '@/lib/utils'

export interface CurrencyInputProps
  extends Omit<
    React.ComponentProps<typeof CurrencyInputBase>,
    'className' | 'onValueChange'
  > {
  value?: string | number
  onValueChange?: (
    value: string | undefined,
    name?: string,
    values?: CurrencyInputOnChangeValues
  ) => void
  className?: string
  'aria-invalid'?: boolean
}

/**
 * CurrencyInput component for VND currency values with automatic thousand separator formatting.
 * 
 * This component wraps react-currency-input-field with VND-specific defaults and styling
 * that matches the existing Input component. It integrates seamlessly with React Hook Form
 * and maintains numeric values in form state while displaying formatted values.
 * 
 * @example
 * ```tsx
 * <CurrencyInput
 *   value={field.value?.toString() || ''}
 *   onValueChange={(value) => {
 *     field.onChange(value ? parseFloat(value) : undefined)
 *   }}
 *   aria-invalid={fieldState.invalid}
 * />
 * ```
 */
const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ className, 'aria-invalid': ariaInvalid, ...props }, ref) => {
    return (
      <CurrencyInputBase
        ref={ref}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          ariaInvalid && 'border-destructive',
          className
        )}
        groupSeparator=","
        decimalSeparator="."
        allowDecimals={false}
        allowNegativeValue={false}
        prefix=""
        suffix=""
        {...props}
      />
    )
  }
)
CurrencyInput.displayName = 'CurrencyInput'

export { CurrencyInput }
